import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import styles from './sellerregispageblock3.module.css';
import { useTranslation } from "react-i18next";
import { encryptImage, imageDataToDataURL } from '../../utils/sensitive-images';
import ImageWithFallback from '../ImageWithFallback';

const SellerRegisPageBlock3 = ({ onBack, onNext, updateData, formData }) => {
  const [frontCCCD, setFrontCCCD] = useState(formData.frontCCCD || null);
  const [frontCCCDEncrypted, setFrontCCCDEncrypted] = useState(formData.frontCCCDEncrypted || null); 
  const [backCCCD, setBackCCCD] = useState(formData.backCCCD || null);
  const [backCCCDEncrypted, setBackCCCDEncrypted] = useState(formData.backCCCDEncrypted || null);
  const [photoWithCCCD, setPhotoWithCCCD] = useState(formData.photoWithCCCD || null);
  const [photoWithCCCDEncrypted, setPhotoWithCCCDEncrypted] = useState(formData.photoWithCCCDEncrypted || null);
  const [cccdNumber, setCccdNumber] = useState(formData.cccdNumber || '');
  const [fullName, setFullName] = useState(formData.fullName || '');
  const [agreePolicy, setAgreePolicy] = useState(formData.agreePolicy || false);
  const [errorMessage, setErrorMessage] = useState('');

  const { t } = useTranslation();

  const handleFileChange = (e, setFile, setEncryptedFile) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    
    reader.onload = function (event) {
        const img = new Image();
        img.onload = function () {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const encryptedData = encryptImage(imageData.data, process.env.REACT_APP_SENSITIVE_ENCRYPT_PASSWORD);
            const encryptedImageData = new ImageData(encryptedData, canvas.width, canvas.height);
            const encryptedDataURL = imageDataToDataURL(encryptedImageData);
            setEncryptedFile(encryptedDataURL);
            setFile(event.target.result);  // Store the base64 string of the original image
        };
        img.src = event.target.result;
    };
    
    reader.readAsDataURL(file);
  };

  const handleAgreePolicyChange = (e) => {
    setAgreePolicy(e.target.checked);
  };

  const validateCccdNumber = (cccdNumber) => {
    const cccdNumberRegex = /^\d{12}$/;
    return cccdNumberRegex.test(cccdNumber);
  };

  const handleNext = () => {
    if (!cccdNumber || !fullName || !frontCCCD || !backCCCD || !photoWithCCCD || !agreePolicy) {
      setErrorMessage(t("fill_all_data"));
      return;
    }
    if (!validateCccdNumber(cccdNumber)) {
      setErrorMessage(t("error_CCCD_number"));
      return;
    }

    // Update data before moving to the next step
    updateData({
      cccdNumber,
      fullName,
      frontCCCD: frontCCCDEncrypted,
      backCCCD: backCCCDEncrypted,
      photoWithCCCD: photoWithCCCDEncrypted,
      agreePolicy,
    });
    setErrorMessage('');
    onNext();
  };

  useEffect(() => {
    setFrontCCCD(formData.frontCCCD || null);
    setFrontCCCDEncrypted(formData.frontCCCDEncrypted || null); 
    setBackCCCD(formData.backCCCD || null);
    setBackCCCDEncrypted(formData.backCCCDEncrypted || null);
    setPhotoWithCCCD(formData.photoWithCCCD || null);
    setPhotoWithCCCDEncrypted(formData.photoWithCCCDEncrypted || null);
    setCccdNumber(formData.cccdNumber || '');
    setFullName(formData.fullName || '');
    setAgreePolicy(formData.agreePolicy || false);
  }, [formData]);

  return (
    <div className={styles["block-container"]}>
      {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
      <div className="mb-3 row">
        <label htmlFor="cccdNumber" className="col-sm-2 col-form-label">{t("CCCD_number")}</label>
        <div className="col-sm-10">
          <input
            type="text"
            id="cccdNumber"
            name="cccdNumber"
            placeholder={t("fill_identification_number")}
            className="form-control"
            value={cccdNumber}
            onChange={(e) => setCccdNumber(e.target.value)}
          />
        </div>
      </div>
      <div className="mb-3 row">
        <label htmlFor="fullName" className="col-sm-2 col-form-label">{t("full_name")}</label>
        <div className="col-sm-10">
          <input
            type="text"
            id="fullName"
            name="fullName"
            placeholder={t("fill_full_name_according_to_CCCD")}
            className="form-control"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-2 col-form-label">{t("CCCD_2sided_photo")}</label>
        <div className="col-sm-10">
          <div className="d-flex mb-3">
            <input
              type="file"
              className="form-control me-2"
              id="frontCCCD"
              accept="image/*"
              onChange={(e) => handleFileChange(e, setFrontCCCD, setFrontCCCDEncrypted)}
            />
            <input
              type="file"
              className="form-control"
              id="backCCCD"
              accept="image/*"
              onChange={(e) => handleFileChange(e, setBackCCCD, setBackCCCDEncrypted)}
            />
          </div>
          <div className="d-flex">
            <div className="me-2">{frontCCCD && <ImageWithFallback loading="lazy" src={frontCCCD} alt="Front of CCCD" className={styles.uploadedImage} />}</div>
            <div>{backCCCD && <ImageWithFallback loading="lazy" src={backCCCD} alt="Back of CCCD" className={styles.uploadedImage} />}</div>
          </div>
        </div>
      </div>
      <div className="mb-3 row">
        <label className="col-sm-2 col-form-label" htmlFor="photoWithCCCD">
          {t("photo_take_with_CCCD")}
        </label>
        <div className="col-sm-10">
          <input
            type="file"
            className="form-control"
            id="photoWithCCCD"
            accept="image/*"
            onChange={(e) => handleFileChange(e, setPhotoWithCCCD, setPhotoWithCCCDEncrypted)}
          />
          {photoWithCCCD && <ImageWithFallback loading="lazy" src={photoWithCCCD} alt="Photo with CCCD" className={`${styles.uploadedImage} mt-3`} />}
        </div>
      </div>
      <div className="mb-3 row">
        <div className="col-sm-2"></div>
        <div className="col-sm-10">
          <input
            className="form-check-input"
            type="checkbox"
            value=""
            id="flexCheckIndeterminate"
            checked={agreePolicy}
            onChange={handleAgreePolicyChange}
          />
          <label className="form-check-label" htmlFor="flexCheckIndeterminate">
            {t("confirm_all")}{' '}
            <a href="https://bhive-dev.vercel.app/privacy-policy">{t("privacy_policy")}</a> {t("of_bhive")}
          </label>
        </div>
      </div>
      <div className="d-flex justify-content-between">
        <button type="button" className="btn btn-success" onClick={onBack}>
          {t("comeback")}
        </button>
        <button type="button" className="btn btn-primary" onClick={handleNext}>
          {t("next")}
        </button>
      </div>
    </div>
  );
};

export default SellerRegisPageBlock3;
